import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography, Paper, Alert, LinearProgress } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useAuth } from '../../contexts/AuthContext';
import { uploadCameraZip } from '../../api/cameraUpload';

interface CameraUploadZipDropzoneProps {
  deviceId: string;
  onUploaded: (batchId: string) => void;
}

const CameraUploadZipDropzone: React.FC<CameraUploadZipDropzoneProps> = ({ deviceId, onUploaded }) => {
  const auth = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const handleFile = useCallback(async (file: File) => {
    setError('');
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setError('Please select a .zip file');
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const accessToken = auth.tokens?.accessToken ?? '';
      const { batchId } = await uploadCameraZip(deviceId, file, accessToken, setProgress);
      onUploaded(batchId);
    } catch (err: any) {
      setError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [auth.tokens?.accessToken, deviceId, onUploaded]);

  return (
    <Box>
      <Paper
        sx={{
          p: 6, textAlign: 'center', cursor: uploading ? 'default' : 'pointer',
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          '&:hover': { borderColor: uploading ? 'divider' : 'primary.main' },
          opacity: uploading ? 0.6 : 1,
        }}
        onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (uploading) return;
          const f = e.dataTransfer.files[0];
          if (f) handleFile(f);
        }}
        onClick={() => { if (!uploading) fileRef.current?.click(); }}
      >
        <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography>{uploading ? `Uploading… ${Math.round(progress * 100)}%` : 'Drop a zip of camera photos here, or click to browse'}</Typography>
        {uploading && (
          <Box sx={{ maxWidth: 320, mx: 'auto', mt: 1 }}>
            <LinearProgress variant="determinate" value={progress * 100} />
          </Box>
        )}
        <Typography variant="caption" color="text.secondary">Supported images: jpg, jpeg, png, webp</Typography>
        <input
          ref={fileRef}
          type="file"
          accept=".zip"
          disabled={uploading}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          style={{ display: 'none' }}
        />
      </Paper>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );
};

export default CameraUploadZipDropzone;
